import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { IProduct } from '@app/models/product.interface';
import { first, map, take, tap } from 'rxjs/operators';
import { Router } from "@angular/router"
import { environment } from '@app/../environments/environment';
import { State, select$ } from '@app/shared/helpers/store';
import { AreasService } from '@app/services/areas/areas.service';
import { CategoriesService } from '@app/services/categories/categories.service'
import { Store } from '@ngrx/store';
import { selectReportsState } from '@app/modules/reports/store/selectors/reports.selectors';
import * as ReportActions from '@app/modules/reports/store/actions/reports.actions';
import { search } from '@app/modules/products/store/actions/products.actions';

@Injectable({
  providedIn: 'root'
})

export class ReportsService {
  public endpoint: string = `${environment.apiUrl}/reports`;
  public productMasterData$: State<any[]>;
  public productMaster$: Observable<IProduct[]>;
  public totalData$: State<number>;
  public pageSizeData$: State<any>;
  public pageSize$: Observable<number>;
  public total$: Observable<any>;
  public currentPageData$: State<number>;
  public currentPage$: Observable<number>;
  public isAllowedToLeaveData$: State<boolean>;
  public isAllowedToLeave$: Observable<any>;
  public isPrdCodeValidData$: State<any>;
  public isPrdCodeValid$: Observable<any>;
  public currentPage;
  public totalPage;
  public productsCount;

  constructor(
    private _http: HttpClient,
    private router: Router,
    private _areas: AreasService,
    private _categoriesService: CategoriesService,
    private store$: Store
  ) {
    this.totalData$ = new State(0);
    this.pageSizeData$ = new State(100);
    this.total$ = select$(this.totalData$, data => data);
    this.pageSize$ = select$(this.pageSizeData$, data => data);
    this.productMasterData$ = new State([]);
    this.productMaster$ = select$(this.productMasterData$, data => data);
    this.isAllowedToLeaveData$ = new State(true);
    this.isAllowedToLeave$ = select$(this.isAllowedToLeaveData$, data => data);
    this.isPrdCodeValidData$ = new State(null);
    this.isPrdCodeValid$ = select$(this.isPrdCodeValidData$, data => data);
  }

  /**
   * The search, category and storage filter the report screen is showing.
   * The page keeps this current so every export asks for the same rows the
   * user is looking at, not the whole catalogue. Ignored when rows are ticked.
   */
  public reportFilters: { search: string, category: number, storages: string } = ReportsService.emptyReportFilters();

  public static emptyReportFilters() {
    return { search: '', category: 0, storages: '' };
  }

  private exportParams(selected: any[]): { [param: string]: string | number } {
    return {
      selected: selected.length > 0 ? selected.join(',') : 0,
      search: this.reportFilters.search || '',
      category: this.reportFilters.category || 0,
      storages: this.reportFilters.storages || ''
    };
  }

  onGenerateTotalOnHandExcel(selected): Observable<any> {
    return this._http.get(`${environment.apiUrl}/reports/total-on-hand/export-excel`,{
      params: this.exportParams(selected)
    });
  }


  onGenerateReorderExcel(selected): Observable<any> {
    return this._http.get(`${environment.apiUrl}/reports/reorder/export-excel`,{
      params: this.exportParams(selected)
    });
  }


  onGeneratePickedExcel(selected): Observable<any> {
    return this._http.get(`${environment.apiUrl}/reports/picked/export-excel`,{
      params: this.exportParams(selected)
    });
  }

  onGenerateReceivedExcel(selected): Observable<any> {
    return this._http.get(`${environment.apiUrl}/reports/received/export-excel`,{
      params: this.exportParams(selected)
    });
  }
  


  onGenerateTotalOnHandPdf(selected) {
    const params = this.exportParams(selected);

    return this._http.get(`${environment.apiUrl}/reports/total-on-hand/export-pdf`, {
      responseType: 'blob',
      params
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }

  onGenerateAssetsPdf(search: string = '') {
    return this._http.get(`${environment.apiUrl}/reports/assets/export-pdf`, {
      responseType: 'blob',
      params: { search: search || '' }
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }


  onGenerateReorderPdf(selected) {
      
    const params = this.exportParams(selected);

    return this._http.get(`${environment.apiUrl}/reports/reorder/export-pdf`, {
      responseType: 'blob',
      params
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }


  onGeneratePickedPdf(selected) {
      
    const params = this.exportParams(selected);

    return this._http.get(`${environment.apiUrl}/reports/picked/export-pdf`, {
      responseType: 'blob',
      params
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }


  onGenerateReceivedPdf(selected) {
      
    const params = this.exportParams(selected);

    return this._http.get(`${environment.apiUrl}/reports/received/export-pdf`, {
      responseType: 'blob',
      params
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }

  getReports(sortType, direction, query?, currentPage?, locID?) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });
    return this._http.get<any>(`${this.endpoint}?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage,
        locID: locID
      }
    });
  }

  /**
   * Date range and the never picked toggle live here so every pick activity
   * call - list, excel and pdf - sends the same filters.
   */
  public pickActivityFilters: { dateFrom: string, dateTo: string, includeNeverPicked: boolean } =
    ReportsService.defaultPickActivityFilters();

  /**
   * Last thirty days, so a request fired before the screen sets its own range
   * still asks for the same window the header shows.
   */
  private static defaultPickActivityFilters() {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 29);

    const asDate = (value: Date) => {
      const month = `${value.getMonth() + 1}`.padStart(2, '0');
      const day = `${value.getDate()}`.padStart(2, '0');
      return `${value.getFullYear()}-${month}-${day}`;
    };

    return { dateFrom: asDate(from), dateTo: asDate(to), includeNeverPicked: false };
  }

  private pickActivityParams(sortType?, direction?, query?) {
    return {
      pageSize: this.pageSizeData$.getValue(),
      sortType: sortType || '',
      direction: direction || 'desc',
      search: query || '',
      page: this.currentPage,
      dateFrom: this.pickActivityFilters.dateFrom,
      dateTo: this.pickActivityFilters.dateTo,
      includeNeverPicked: this.pickActivityFilters.includeNeverPicked ? 1 : 0
    };
  }

  getReportsPickActivity(sortType, direction, query?) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });

    return this._http.get<any>(`${this.endpoint}/pick-activity?page=${this.currentPage}`, {
      params: this.pickActivityParams(sortType, direction, query)
    });
  }

  onGeneratePickActivityExcel(): Observable<any> {
    return this._http.get(`${environment.apiUrl}/reports/pick-activity/export-excel`, {
      params: this.pickActivityParams(null, null, this.reportFilters.search)
    });
  }

  onGeneratePickActivityPdf() {
    return this._http.get(`${environment.apiUrl}/reports/pick-activity/export-pdf`, {
      responseType: 'blob',
      params: this.pickActivityParams(null, null, this.reportFilters.search)
    }).pipe(first()).subscribe((data: Blob) => {
      let pdf = new Blob([data], { type: 'application/pdf' });
      let url = URL.createObjectURL(pdf);
      window.open(url);
    });
  }

  getReportsPicked(sortType, direction, query?, currentPage?) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });	
    return this._http.get<any>(`${this.endpoint}/picked?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }

  getReportsProductSerialNumber(sortType, direction, query?, currentPage?, prdID?,totalOnly?) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });	
    return this._http.get<any>(`${this.endpoint}/product-serial-number?page=${this.currentPage}`, {
      params: {
        ...(prdID ? { prdID: prdID } : {}),
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query ?? '',
        page: this.currentPage,
        ...(totalOnly ? { totalOnly: totalOnly } : {})
      }
    });
  }

  getReportsReceived(sortType, direction, query?, currentPage?) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => {
      this.currentPage = currentPage < 0 ? 0 : currentPage;
    });	
    return this._http.get<any>(`${this.endpoint}/received?page=${this.currentPage}`, {
      params: {
        pageSize: this.pageSizeData$.getValue(),
        sortType: sortType,
        direction: direction,
        search: query,
        page: this.currentPage
      }
    });
  }

  getReportsReorder(sortType, direction, query?, currentPage?, locID?) {
    // Fetch the full reorder dataset in a single call so the backend's
    // sort (including PHP-side post-processing for Current Balance and
    // Re-Order columns) applies across the entire result set instead of
    // only the current page. The reorder paginator is hidden in the UI;
    // users scroll through the full list.
    return this._http.get<any>(`${this.endpoint}/reorder?page=0`, {
      params: {
        pageSize: 10000,
        sortType: sortType,
        direction: direction,
        search: query,
        page: 0,
        locID: locID
      }
    });
  }

  getReport(id: number): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/${id}`)
  }

  searchReports(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search`, {
      params: {
        query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  searchPicked(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search-picked`, {
      params: {
        search: query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  searchProductSerialNumber(sort, direction, query: string, prdID:any): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/product-serial-number`, {
      params: {
        ...(prdID ? { prdID: prdID } : {}),
        search: query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }


  searchReceived(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search-received`, {
      params: {
        search: query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }
  searchReorder(sort, direction, query: string): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/search-reorder`, {
      params: {
        search: query,
        pageSize: this.pageSizeData$.getValue(),
        sortType: sort ? sort : 'prdNameEn',
        direction: direction ? direction : 'ASC',
        page: this.currentPage
      }
    });
  }

  public setOnOrder(product_data: any): Promise<any> {
    return this._http.post<any>(`${this.endpoint}/set-onorder`, product_data).toPromise();
  }

  // Products marked "On Order" (starred) at the active location (dashboard widget).
  public getOnOrder() {
    return this._http.get<any>(`${this.endpoint}/on-order`);
  }
  


  searchReportsByCategory(query: string) {
    let id;
    this._categoriesService.currentCategory$.pipe(first()).subscribe(res => {
      id = res;
    });
    if (id !== 0) {
      this._http.get<any>(`${this.endpoint}/search/category/${id}`, {
        params: {
          query
        }
      }).pipe(first()).subscribe((data) => {
        this.totalData$.next(data.length);
      });
    } else {
      this.store$.dispatch(ReportActions.search({ query: query }))
    }
  }

  getReportsByStorages(id, active, direction) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/storages/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction,
        // A category picked before the storage keeps narrowing the list.
        category: this.reportFilters.category || 0
      }
    })
  }

  getReportsByCategory(id, active, direction) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/categories/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction
      }
    });
  }

  getReorderByStorages(id, active, direction) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/reorder/storages/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction
      }
    })
  }

  getReportsByReorderCategory(id, active, direction) {
    this.store$.select(selectReportsState).pipe(first()).subscribe(({ currentPage }) => this.currentPage = currentPage);
    return this._http.get<any>(`${this.endpoint}/reorder/categories/${id}`, {
      params: {
        currentPage: this.currentPage,
        active: active,
        direction: direction
      }
    });
  }
  /** Serialized products report (T-317). Empty filter values are not sent. */
  getSerialized(filters: { [key: string]: string | number | null | undefined }): Observable<any> {
    return this._http.get<any>(`${this.endpoint}/serialized`, { params: this.serializedParams(filters) });
  }

  /** Whole result of the current filters as a file (csv or xlsx). */
  exportSerialized(filters: { [key: string]: string | number | null | undefined }, format: 'csv' | 'xlsx'): Observable<HttpResponse<Blob>> {
    return this._http.get(`${this.endpoint}/serialized/export`, {
      params: this.serializedParams(filters).set('format', format),
      responseType: 'blob',
      observe: 'response'
    });
  }

  private serializedParams(filters: { [key: string]: string | number | null | undefined }): HttpParams {
    let params = new HttpParams();
    Object.keys(filters).forEach(key => {
      const value = filters[key];
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    });
    return params;
  }
}

